import { call, put, delay } from "redux-saga/effects";
import PaymentScheduleActions from "./payment-schedule.reducer";
import * as api from "../../../Service/api";
import {
	buildMomoPaymentFormData,
	isInvalidMomoAccountResponse,
} from "../../../utils/momoPaymentUtils";

const PAYMENT_REQUEST_POLL_INTERVAL_MS = 3000;
const PAYMENT_REQUEST_POLL_MAX_ATTEMPTS = 60;

function* pollPaymentRequestVerification(paymentRequestId) {
	for (let attempt = 0; attempt < PAYMENT_REQUEST_POLL_MAX_ATTEMPTS; attempt++) {
		const response = yield call(api.verifyPaymentRequest, paymentRequestId);
		if (response.ok) {
			return response;
		}
		if (attempt < PAYMENT_REQUEST_POLL_MAX_ATTEMPTS - 1) {
			yield delay(PAYMENT_REQUEST_POLL_INTERVAL_MS);
		}
	}
	return null;
}

function* triggerMomoPaymentWithVerification(triggerApi, successAction, failureAction, pollingAction, action, ...triggerArgs) {
	const { businessId, provider, formData } = action;
	const paymentPayload = buildMomoPaymentFormData(formData);
	const triggerArgsWithPayload = [...triggerArgs];
	triggerArgsWithPayload[triggerArgsWithPayload.length - 1] = paymentPayload;

	const accountResponse = yield call(api.fetchProviderAccount, provider, businessId, paymentPayload);
	if (!accountResponse.ok) {
		const errorPayload = accountResponse.data || {};
		yield put(failureAction({
			...errorPayload,
			...(isInvalidMomoAccountResponse(accountResponse) && !errorPayload.detail && !errorPayload.message
				? { messageKey: "plan.invalid_momo_account" }
				: {}),
		}));
		return;
	}

	const triggerResponse = yield call(triggerApi, ...triggerArgsWithPayload);
	if (!triggerResponse.ok) {
		yield put(failureAction(triggerResponse.data));
		return;
	}

	const paymentRequestId = triggerResponse.data?.id;
	if (!paymentRequestId) {
		yield put(failureAction({ messageKey: "plan.collect_failure" }));
		return;
	}

	yield put(pollingAction(triggerResponse.data));

	const verifyResponse = yield call(pollPaymentRequestVerification, paymentRequestId);
	if (!verifyResponse?.ok) {
		yield put(failureAction({ messageKey: "plan.momo_payment_verification_timeout" }));
		return;
	}

	yield put(successAction(triggerResponse.data));
}

export function* getPaymentSchedule(action) {
	const { paymentScheduleId } = action;
	// make the call to the api
	const response = yield call(api.getPaymentSchedule, paymentScheduleId);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.paymentScheduleSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.paymentScheduleFailure(response.data));
	}
}

export function* getPaymentScheduleProjection(action) {
	const { businessId, startDate, endDate } = action;
	// make the call to the api

	const response = yield call(
		api.getPaymentScheduleProjection,
		businessId,
		startDate,
		endDate
	);

	// success?
	if (response.ok) {
		yield put(
			PaymentScheduleActions.paymentScheduleProjectionSuccess(response.data)
		);
	} else {
		yield put(
			PaymentScheduleActions.paymentScheduleProjectionFailure(response.data)
		);
	}
}

export function* getPaymentSchedules(action) {
	const { subscriberId, options } = action;
	// make the call to the api

	const response = yield call(api.getPaymentSchedules, subscriberId, options);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.paymentScheduleAllSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.paymentScheduleAllFailure(response.data));
	}
}
export function* getPaymentSchedulesMissed(action) {
	const { subscriberId, options } = action;
	// make the call to the api

	const response = yield call(api.getSubscriptionMissedSchedule, subscriberId, options);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.paymentScheduleMissedSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.paymentScheduleMissedFailure(response.data));
	}
}
export function* getPaymentSchedulesPending(action) {
	const { subscriberId, options } = action;
	// make the call to the api

	const response = yield call(api.getSubscriptionPendingSchedule, subscriberId, options);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.paymentSchedulePendingSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.paymentSchedulePendingFailure(response.data));
	}
}

export function* getRecentPendingPaymentScheduleForSubscription(action) {
	const { subscriptionId } = action;

	const response = yield call(api.getRecentPendingPaymentScheduleForSubscription, subscriptionId);

	if (response.ok) {
		yield put(PaymentScheduleActions.recentPendingPaymentScheduleSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.recentPendingPaymentScheduleFailure(response.data));
	}
}

export function* getPaymentScheduleRange(action) {
	const { businessId, startDate, endDate, options } = action;
	// make the call to the api

	const response = yield call(
		api.getPaymentScheduleRange,
		businessId,
		startDate,
		endDate,
		options
	);

	// success?
	if (response.ok) {
		yield put(
			PaymentScheduleActions.paymentScheduleRangeSuccess(response.data)
		);
	} else {
		yield put(
			PaymentScheduleActions.paymentScheduleRangeFailure(response.data)
		);
	}
}

export function* getPaymentScheduleSummary(action) {
	const { businessId, startDate, endDate } = action;

	// make the call to the api
	const response = yield call(
		api.getPaymentScheduleSummary,
		businessId,
		startDate,
		endDate,
	);

	// success?
	if (response.ok) {
		yield put(
			PaymentScheduleActions.paymentScheduleSummarySuccess(response.data)
		);
	} else {
		yield put(
			PaymentScheduleActions.paymentScheduleSummaryFailure(response.data)
		);
	}
}
export function* getWeeklyPaymentScheduleSummary(action) {
	const { businessId, startDate, endDate } = action;

	// make the call to the api
	const response = yield call(
		api.getWeeklyPaymentScheduleSummary,
		businessId,
		startDate,
		endDate,
	);

	// success?
	if (response.ok) {
		yield put(
			PaymentScheduleActions.weeklyPaymentScheduleSummarySuccess(response.data)
		);
	} else {
		yield put(
			PaymentScheduleActions.weeklyPaymentScheduleSummaryFailure(response.data)
		);
	}
}
export function* getThreeMonthsPaymentScheduleSummary(action) {
	const { businessId, startDate, endDate } = action;

	// make the call to the api
	const response = yield call(
		api.get3MonthsPaymentScheduleSummary,
		businessId,
		startDate,
		endDate,
	);

	// success?
	if (response.ok) {
		yield put(
			PaymentScheduleActions.threeMonthsPaymentScheduleSummarySuccess(response.data)
		);
	} else {
		yield put(
			PaymentScheduleActions.threeMonthsPaymentScheduleSummaryFailure(response.data)
		);
	}
}

export function* getSixMonthsPaymentScheduleSummary(action) {
	const { businessId, startDate, endDate } = action;

	// make the call to the api
	const response = yield call(
		api.get6MonthsPaymentScheduleSummary,
		businessId,
		startDate,
		endDate,
	);

	// success?
	if (response.ok) {
		yield put(
			PaymentScheduleActions.sixMonthsPaymentScheduleSummarySuccess(response.data)
		);
	} else {
		yield put(
			PaymentScheduleActions.sixMonthsPaymentScheduleSummaryFailure(response.data)
		);
	}
}

export function* getTwelveMonthsPaymentScheduleSummary(action) {
	const { businessId, startDate, endDate } = action;

	// make the call to the api
	const response = yield call(
		api.get12MonthsPaymentScheduleSummary,
		businessId,
		startDate,
		endDate,
	);

	// success?
	if (response.ok) {
		yield put(
			PaymentScheduleActions.twelveMonthsPaymentScheduleSummarySuccess(response.data)
		);
	} else {
		yield put(
			PaymentScheduleActions.twelveMonthsPaymentScheduleSummaryFailure(response.data)
		);
	}
}

export function* updatePaymentSchedule(action) {
	const { paymentSchedule } = action;
	// make the call to the api
	const idIsNotNull = !!paymentSchedule.id;
	const response = yield call(
		idIsNotNull ? api.updatePaymentSchedule : api.createPaymentSchedule,
		paymentSchedule
	);

	// success?
	if (response.ok) {
		yield put(
			PaymentScheduleActions.paymentScheduleUpdateSuccess(response.data)
		);
	} else {
		yield put(
			PaymentScheduleActions.paymentScheduleUpdateFailure(response.data)
		);
	}
}

export function* reassignPaymentSchedulePlan(action) {
	const { paymentScheduleId, planId, membershipId } = action;
	const response = yield call(
		api.reassignPaymentSchedulePlan,
		paymentScheduleId,
		planId,
		membershipId
	);

	if (response.ok) {
		yield put(PaymentScheduleActions.reassignPaymentSchedulePlanSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.reassignPaymentSchedulePlanFailure(response.data));
	}
}

export function* notifyPaymentSchedule(action) {
	const { paymentScheduleId } = action;
	// make the call to the api

	const response = yield call(api.notifyPaymentSchedule, paymentScheduleId);

	// success?
	if (response.ok) {
		yield put(
			PaymentScheduleActions.paymentScheduleNotifySuccess(response.data)
		);
	} else {
		yield put(
			PaymentScheduleActions.paymentScheduleNotifyFailure(response.data)
		);
	}
}

export function* deletePaymentSchedule(action) {
	const { paymentScheduleId } = action;
	// make the call to the api

	const response = yield call(api.deletePaymentSchedule, paymentScheduleId);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.paymentScheduleDeleteSuccess());
	} else {
		yield put(
			PaymentScheduleActions.paymentScheduleDeleteFailure(response.data)
		);
	}
}

export function* getDailyCharts(action) {
	const { businessId, startDate, endDate } = action
	//make the call to the api 

	const response = yield call(api.getDailyCharts, businessId, startDate, endDate);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.dailyChartsSuccess(response.data))
	} else {
		yield put(PaymentScheduleActions.dailyChartsFailure(response.data))
	}
}
export function* getWeeklyCharts(action) {
	const { businessId, startDate, endDate } = action
	//make the call to the api 

	const response = yield call(api.getWeeklyCharts, businessId, startDate, endDate);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.weeklyChartsSuccess(response.data))
	} else {
		yield put(PaymentScheduleActions.weeklyChartsFailure(response.data))
	}
}

export function* getThreeMonthsCharts(action) {
	const { businessId, startDate, endDate } = action
	//make the call to the api 

	const response = yield call(api.get3MonthlyCharts, businessId, startDate, endDate);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.threeMonthsChartsSuccess(response.data))
	} else {
		yield put(PaymentScheduleActions.threeMonthsChartsFailure(response.data))
	}
}

export function* getSixMonthsCharts(action) {
	const { businessId, startDate, endDate } = action
	//make the call to the api 

	const response = yield call(api.get6MonthlyCharts, businessId, startDate, endDate);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.sixMonthsChartsSuccess(response.data))
	} else {
		yield put(PaymentScheduleActions.sixMonthsChartsFailure(response.data))
	}
}

export function* getTwelveMonthsCharts(action) {
	const { businessId, startDate, endDate } = action
	//make the call to the api 

	const response = yield call(api.get12MonthlyCharts, businessId, startDate, endDate);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.twelveMonthsChartsSuccess(response.data))
	} else {
		yield put(PaymentScheduleActions.twelveMonthsChartsFailure(response.data))
	}
}

export function* getMissedSchedulesCount(action) {
	const { subscriberId, options } = action
	//make the call to the api 

	const response = yield call(api.getSubscriptionMissedScheduleCount, subscriberId, options);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.missedPaymentSchedulesCountSuccess(response.data))
	} else {
		yield put(PaymentScheduleActions.missedPaymentSchedulesCountFailure(response.data))
	}
}

export function* getPaymentMissedCsv(action) {
	const { businessId, query, options } = action;

	// make the call to the api
	const response = yield call(api.getMissedPaymentCSV, businessId, query, options);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.paymentMissedCsvSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.paymentMissedCsvFailure(response.data));
	}
}

export function* getPaymentExternalCsv(action) {
	const { businessId, query, options } = action;

	// make the call to the api
	const response = yield call(api.getExternalPaymentCSV, businessId, query, options);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.paymentExternalCsvSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.paymentExternalCsvFailure(response.data));
	}
}
export function* getPaymentMissedPdf(action) {
	const { businessId, membershipId, query, options } = action;

	// make the call to the api
	const response = yield call(api.getMissedPaymentPDF, businessId, membershipId, query, options);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.paymentMissedPdfSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.paymentMissedPdfFailure(response.data));
	}
}
export function* getPaymentExternalPdf(action) {
	const { businessId, membershipId, query, options } = action;

	// make the call to the api
	const response = yield call(api.getExternalPaymentPDF, businessId, membershipId, query, options);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.paymentExternalPdfSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.paymentExternalPdfFailure(response.data));
	}
}

export function* recordExternalPayment(action) {
	const { planId, membershipId, formData } = action;

	// make the call to the api
	const response = yield call(api.externalPaymentBySubscriber, planId, membershipId, formData);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.externalPaymentBySubscriberSuccess(response.ok));
	} else {
		yield put(PaymentScheduleActions.externalPaymentBySubscriberFailure(response.ok));
	}
}

export function* recordExternalPaymentBySubscription(action) {
	const { subscriptionId, membershipId, formData } = action;

	// make the call to the api
	const response = yield call(api.externalPaymentPerSubscription, subscriptionId, membershipId, formData);

	// success?
	if (response.ok) {
		yield put(PaymentScheduleActions.externalPaymentBySubscriptionSuccess(response.ok));
	} else {
		yield put(PaymentScheduleActions.externalPaymentBySubscriptionFailure(response.problem));
	}
}

export function* triggerMomoPaymentByPlan(action) {
	const { planId, businessCustomerId, membershipId, formData } = action;

	yield* triggerMomoPaymentWithVerification(
		api.triggerMomoPaymentByPlan,
		PaymentScheduleActions.triggerMomoPaymentByPlanSuccess,
		PaymentScheduleActions.triggerMomoPaymentByPlanFailure,
		PaymentScheduleActions.triggerMomoPaymentByPlanPolling,
		action,
		planId,
		businessCustomerId,
		membershipId,
		formData
	);
}

export function* triggerMomoPaymentBySchedule(action) {
	const { scheduleId, businessCustomerId, membershipId, formData } = action;

	yield* triggerMomoPaymentWithVerification(
		api.triggerMomoPaymentBySchedule,
		PaymentScheduleActions.triggerMomoPaymentByScheduleSuccess,
		PaymentScheduleActions.triggerMomoPaymentByScheduleFailure,
		PaymentScheduleActions.triggerMomoPaymentBySchedulePolling,
		action,
		scheduleId,
		businessCustomerId,
		membershipId,
		formData
	);
}

export function* triggerMomoPaymentBySubscriber(action) {
	const { subscriberId, businessCustomerId, membershipId, formData } = action;

	yield* triggerMomoPaymentWithVerification(
		api.triggerMomoPaymentBySubscriber,
		PaymentScheduleActions.triggerMomoPaymentBySubscriberSuccess,
		PaymentScheduleActions.triggerMomoPaymentBySubscriberFailure,
		PaymentScheduleActions.triggerMomoPaymentBySubscriberPolling,
		action,
		subscriberId,
		businessCustomerId,
		membershipId,
		formData
	);
}

export function* getAccountsPaymentSearch(action) {
	const { businessId, filter, options } = action;
	const response = yield call(api.getAccountsPaymentSearch, businessId, filter, options);
	if (response.ok) {
		yield put(PaymentScheduleActions.accountsPaymentSearchSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.accountsPaymentSearchFailure(response.data));
	}
}

export function* getAccountsPaymentCsv(action) {
	const { businessId, query, options } = action;
	const response = yield call(api.getAccountsPaymentCSV, businessId, query, options);
	if (response.ok) {
		yield put(PaymentScheduleActions.accountsPaymentCsvSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.accountsPaymentCsvFailure(response.data));
	}
}

export function* getAccountsPaymentPdf(action) {
	const { businessId, membershipId, query, options } = action;
	const response = yield call(api.getAccountsPaymentPDF, businessId, membershipId, query, options);
	if (response.ok) {
		yield put(PaymentScheduleActions.accountsPaymentPdfSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.accountsPaymentPdfFailure(response.data));
	}
}

export function* getAccountsPaymentTotals(action) {
	const { businessId, query, options } = action;
	const response = yield call(api.getAccountsPaymentTotals, businessId, query, options);
	if (response.ok) {
		yield put(PaymentScheduleActions.accountsPaymentTotalsSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.accountsPaymentTotalsFailure(response.data));
	}
}

export function* getExternalPaymentTotals(action) {
	const { businessId, query, options } = action;
	const response = yield call(api.getExternalPaymentTotals, businessId, query, options);
	if (response.ok) {
		yield put(PaymentScheduleActions.externalPaymentTotalsSuccess(response.data));
	} else {
		yield put(PaymentScheduleActions.externalPaymentTotalsFailure(response.data));
	}
}

